/**
 * 기업 관리 — 자사 현황
 * ---------------------------------------------------------------------------
 * 기업 관리자가 로그인하면 처음 보는 화면이다.
 *
 * 배치 순서에 의도가 있다.
 *   1. 조치가 필요한 것 (한도 초과, 미납, 로그인 실패 급증, 열린 알람)
 *   2. 사람      구성원 상태와 지금 접속 중인 수
 *   3. 사용량    이번 달 한도 대비
 *   4. 구독 제품과 최근 활동
 *
 * 구성원 명부를 여기에 늘어놓지 않는다. 이름·연락처는 「구성원」 화면의
 * 일이고, 첫 화면은 "지금 신경 쓸 게 있나" 에만 답한다.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { CodeTag, useCodes } from '../../lib/codes';
import { AiWeeklyReportButton } from './AiWeeklyReportModal';
import { OnboardingBanner } from '../../components/OnboardingBanner';

const { Text, Title, Paragraph } = Typography;

interface Quota {
  metricKey: string;
  metricName: string;
  /** 제품별로 따로 세는 지표면 그 제품 이름. 기업 전체 지표는 null. */
  productName: string | null;
  unit: string;
  currentValue: number;
  limitValue: number | null;
  isExceeded: boolean;
  /** 포함량 없이 쓴 만큼 내는 지표인가 (AI 보고서 등). 초과로 세지 않는다. */
  isPayPerUse: boolean;
  /** 초과해도 막히지 않고 추가 청구되는가. */
  overageAllowed: boolean;
}

interface ProductRow {
  productCode: string;
  productName: string;
  /** 제품 접속 주소. 등록 전이면 null 이라 바로가기를 잠근다. */
  baseUrl: string | null;
  planName: string;
  status: string;
  units: number;
  currentPeriodEnd: string | null;
}

interface OpenAlert {
  alertEventId: string;
  severity: string;
  message: string;
  firedAt: string;
}

/**
 * 볼 수 없는 칸은 null 로 온다.
 *
 * `?? 0` 으로 뭉개면 안 된다 — 권한이 없어 못 받은 것을 「미수금 0원」으로
 * 단언하게 되고, 그 말은 틀릴 수 있다. null 이면 카드째 그리지 않는다.
 */
interface Dashboard {
  companyName: string;
  status: string;
  userTotal: number | null;
  userActive: number | null;
  userInvited: number | null;
  userLocked: number | null;
  sessionsLive: number | null;
  usersToday: number | null;
  loginFailures7d: number | null;
  quotas: Quota[];
  products: ProductRow[] | null;
  outstandingAmount: number | null;
  overdueCount: number | null;
  openAlerts: OpenAlert[] | null;
}

interface Activity {
  auditLogId: string;
  occurredAt: string;
  category: string;
  action: string;
  actorType: string;
  resourceLabel: string | null;
  result: string;
}

/** 표시명과 색은 공통코드가 정한다(「시스템 관리 > 공통코드」). */
const CODE_GROUPS = ['ALERT_SEVERITY', 'SUBSCRIPTION_STATUS', 'AUDIT_CATEGORY', 'AUDIT_RESULT'];

/** 한도 표시는 단위에 따라 다르게 읽힌다. 바이트는 사람이 읽는 단위로 바꾼다. */
function amount(value: number, unit: string): string {
  if (unit !== 'BYTES') return value.toLocaleString();
  if (!value) return '0';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
  return `${(value / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function TenantDashboardPage() {
  const codes = useCodes(CODE_GROUPS);

  const dashboard = useQuery<Dashboard, NormalizedError>({
    queryKey: ['tenant', 'dashboard'],
    queryFn: async () => (await apiClient.get<Dashboard>('/tenant/dashboard')).data,
  });

  const activity = useQuery<Activity[], NormalizedError>({
    queryKey: ['tenant', 'dashboard', 'activity'],
    queryFn: async () =>
      (await apiClient.get<Activity[]>('/tenant/dashboard/activity', { params: { limit: 10 } }))
        .data,
    // 감사로그 조회 권한이 없는 관리자도 있다. 그 경우 카드만 비운다.
    retry: false,
  });

  const data = dashboard.data;
  const exceeded = (data?.quotas ?? []).filter((q) => q.isExceeded);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/*
        사업자등록증·결제 수단 안내.
        **맨 위**에 둔다 — 로그인하면 처음 보는 화면이고, 기한이 걸린 일은
        숫자 카드를 지나쳐 스크롤해야 보이면 안 읽힌다.
      */}
      <OnboardingBanner />

      <Row align="middle" justify="space-between">
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {data?.companyName ?? '자사 현황'}
          </Title>
          <Text type="secondary">오늘 신경 쓸 것이 있는지 한눈에 봅니다.</Text>
        </Col>
        <Col>
          <Space>
            <AiWeeklyReportButton />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                void dashboard.refetch();
                void activity.refetch();
              }}
              loading={dashboard.isFetching}
            >
              새로고침
            </Button>
          </Space>
        </Col>
      </Row>

      {dashboard.isError && <Alert type="error" showIcon message={errorText(dashboard.error)} />}

      {/* ---- 1. 조치가 필요한 것 ---- */}
      {exceeded.length > 0 && (
        <Alert
          type={exceeded.some((q) => !q.overageAllowed) ? 'error' : 'warning'}
          showIcon
          message={`사용량 한도를 넘긴 항목이 ${exceeded.length}개 있습니다`}
          description={
            exceeded
              .map((q) => (q.productName ? `${q.productName} ${q.metricName}` : q.metricName))
              .join(', ') +
            (exceeded.some((q) => !q.overageAllowed)
              ? ' — 초과 사용이 허용되지 않는 항목이 있어 해당 기능이 막힙니다.'
              : ' — 초과분은 다음 청구서에 추가됩니다. 기능이 막히지는 않습니다.')
          }
          action={
            <Link to="/app/billing">
              <Button size="small">사용량 보기</Button>
            </Link>
          }
        />
      )}

      {(data?.overdueCount ?? 0) > 0 && (
        <Alert
          type="error"
          showIcon
          message={`연체된 청구서가 ${data?.overdueCount}건 있습니다`}
          description="납부가 늦어지면 서비스가 제한될 수 있습니다."
          action={
            <Link to="/app/billing">
              <Button size="small" danger>
                청구서 보기
              </Button>
            </Link>
          }
        />
      )}

      {(data?.openAlerts?.length ?? 0) > 0 && (
        <Card size="small" title="열린 알람">
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {data!.openAlerts!.map((a) => (
              <Space key={a.alertEventId}>
                <CodeTag codes={codes} group="ALERT_SEVERITY" value={a.severity} />
                <Text>{a.message}</Text>
                <Text type="secondary">{dayjs(a.firedAt).format('MM-DD HH:mm')}</Text>
              </Space>
            ))}
          </Space>
        </Card>
      )}

      {/*
        ---- 2. 사람 ----
        값이 null 이면 그 칸을 볼 권한이 없다는 뜻이다. 카드째 뺀다 —
        `?? 0` 으로 그리면 「미납 0원」처럼 **틀릴 수도 있는 말**을 하게 된다.
        네 칸이 모두 빠지면 줄도 그리지 않는다.
      */}
      {(data?.userTotal !== null ||
        data?.sessionsLive !== null ||
        data?.outstandingAmount !== null) && (
        <Row gutter={[16, 16]}>
          {data?.userTotal !== null && (
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="구성원" value={data?.userTotal ?? 0} suffix="명" />
                <Text type="secondary">
                  활성 {data?.userActive ?? 0} · 초대중 {data?.userInvited ?? 0}
                  {(data?.userLocked ?? 0) > 0 && (
                    <Text type="danger"> · 잠김 {data?.userLocked}</Text>
                  )}
                </Text>
              </Card>
            </Col>
          )}
          {data?.sessionsLive !== null && (
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="지금 접속 중" value={data?.sessionsLive ?? 0} suffix="세션" />
                <Text type="secondary">최근 24시간 {data?.usersToday ?? 0}명 로그인</Text>
              </Card>
            </Col>
          )}
          {data?.loginFailures7d !== null && (
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="로그인 실패 (7일)"
                  value={data?.loginFailures7d ?? 0}
                  suffix="회"
                  valueStyle={(data?.loginFailures7d ?? 0) > 50 ? { color: '#cf1322' } : undefined}
                />
                <Link to="/app/logs">
                  <Text type="secondary">로그 조회 →</Text>
                </Link>
              </Card>
            </Col>
          )}
          {data?.outstandingAmount !== null && (
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="미납 금액"
                  value={data?.outstandingAmount ?? 0}
                  suffix="원"
                  valueStyle={(data?.overdueCount ?? 0) > 0 ? { color: '#cf1322' } : undefined}
                />
                <Text type="secondary">연체 {data?.overdueCount ?? 0}건</Text>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* ---- 3. 사용량 ---- */}
      <Card size="small" title="이번 달 사용량" loading={dashboard.isLoading}>
        {(data?.quotas.length ?? 0) === 0 ? (
          <Empty description="집계된 사용량이 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Row gutter={[16, 16]}>
            {data!.quotas.map((q) => {
              const ratio = q.limitValue ? Math.min(100, (q.currentValue / q.limitValue) * 100) : 0;
              return (
                <Col xs={24} md={12} xl={8} key={q.metricKey}>
                  <Text>
                    {q.metricName}
                    {q.productName && <Text type="secondary"> · {q.productName}</Text>}
                  </Text>
                  {/*
                    쓴 만큼 내는 지표(AI 보고서 등)는 막대를 그리지 않는다.
                    포함량이 0이라 분모가 없고, 막대로 그리면 한 건만 써도
                    100% 빨강이 된다 — 멀쩡한 사용이 사고처럼 보인다.
                  */}
                  {q.isPayPerUse ? (
                    <div style={{ padding: '6px 0' }}>
                      <Text strong style={{ fontSize: 16 }}>
                        {amount(q.currentValue, q.unit)}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {' '}
                        · 사용한 만큼 청구
                      </Text>
                    </div>
                  ) : (
                    <Progress
                      percent={Math.round(ratio)}
                      status={q.isExceeded ? 'exception' : ratio > 90 ? 'active' : 'normal'}
                      format={() =>
                        q.limitValue
                          ? `${amount(q.currentValue, q.unit)} / ${amount(q.limitValue, q.unit)}`
                          : amount(q.currentValue, q.unit)
                      }
                    />
                  )}
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      {/*
        ---- 4. 구독과 최근 활동 ----
        구독 카드는 계약 수량을 담고 있어 과금을 볼 수 있는 사람에게만 보인다.
        빠지면 「최근 활동」이 가로를 다 쓴다 — 반쪽짜리 빈자리를 남기지 않는다.
      */}
      <Row gutter={[16, 16]}>
        {data?.products != null && (
        <Col xs={24} xl={10}>
          <Card
            size="small"
            title="구독 중인 제품"
            extra={
              <Link to="/app/store">
                <Button size="small" type="link">
                  제품 둘러보기
                </Button>
              </Link>
            }
          >
            <Table<ProductRow>
              rowKey="productCode"
              size="small"
              pagination={false}
              dataSource={data.products}
              locale={{
                emptyText: <Empty description="구독 중인 제품이 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
              }}
              columns={[
                { title: '제품', dataIndex: 'productName' },
                { title: '요금제', dataIndex: 'planName', width: 110 },
                {
                  title: '상태',
                  dataIndex: 'status',
                  width: 90,
                  render: (v: string) => (
                    <CodeTag codes={codes} group="SUBSCRIPTION_STATUS" value={v} />
                  ),
                },
                {
                  title: '다음 결제',
                  dataIndex: 'currentPeriodEnd',
                  width: 105,
                  render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
                },
                /*
                  바로가기.
                  이 화면에 매일 들어오는 이유는 현황 확인이고, 그 다음에 하는
                  일은 제품으로 넘어가는 것이다. 「제품」 화면까지 한 번 더
                  들르게 할 이유가 없다.
                */
                {
                  title: '',
                  key: 'go',
                  width: 86,
                  render: (_, row) =>
                    row.baseUrl ? (
                      <Button
                        size="small"
                        type="link"
                        icon={<ExportOutlined />}
                        href={row.baseUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        바로가기
                      </Button>
                    ) : (
                      <Tooltip title="아직 접속 주소가 등록되지 않았습니다">
                        <Button size="small" type="link" disabled>
                          바로가기
                        </Button>
                      </Tooltip>
                    ),
                },
              ]}
            />
          </Card>
        </Col>
        )}

        <Col xs={24} xl={data?.products != null ? 14 : 24}>
          <Card
            size="small"
            title="최근 활동"
            extra={
              <Link to="/app/logs">
                <Button size="small" type="link">
                  전체 보기
                </Button>
              </Link>
            }
          >
            {activity.isError ? (
              <Paragraph type="secondary">
                감사로그를 볼 권한이 없습니다. 기업 관리자에게 요청하세요.
              </Paragraph>
            ) : (
              <Table<Activity>
                rowKey="auditLogId"
                size="small"
                pagination={false}
                loading={activity.isFetching}
                dataSource={activity.data ?? []}
                locale={{
                  emptyText: <Empty description="최근 활동이 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                }}
                columns={[
                  {
                    title: '일시',
                    dataIndex: 'occurredAt',
                    width: 140,
                    render: (v: string) => dayjs(v).format('MM-DD HH:mm:ss'),
                  },
                  {
                    title: '분류',
                    dataIndex: 'category',
                    width: 90,
                    render: (v: string) => (
                      <CodeTag codes={codes} group="AUDIT_CATEGORY" value={v} />
                    ),
                  },
                  { title: '동작', dataIndex: 'action', width: 170 },
                  { title: '대상', dataIndex: 'resourceLabel', ellipsis: true },
                  {
                    title: '결과',
                    dataIndex: 'result',
                    width: 80,
                    render: (v: string) => (
                      <CodeTag codes={codes} group="AUDIT_RESULT" value={v} />
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
